import type { FC } from 'react';
import type { components } from '@/generated/internal-api/openapi-types';
import { _isNotEmpty } from '@/littledash';
import { useModalAction } from '@/utils/modal';

type TreatmentTypeGlossaryStudy = components['schemas']['TreatmentTypeGlossaryStudyV1.schema'];

interface TreatmentStudyUsageProps {
  treatmentName: string;
  studies?: Array<TreatmentTypeGlossaryStudy>;
  testId?: string;
}

/**
 * Table cell describing how widely a treatment type is used. The count is a button that opens a
 * scrollable modal listing the studies, as a treatment can be used in hundreds of them. Renders a
 * muted, non-interactive "Unused" when the treatment is used in no studies.
 */
const TreatmentStudyUsage: FC<TreatmentStudyUsageProps> = ({
  treatmentName,
  studies,
  testId = 'treatment-study-usage',
}) => {
  const { openModal } = useModalAction();

  if (_isNotEmpty(studies)) {
    const countLabel = `${studies.length} ${studies.length === 1 ? 'study' : 'studies'}`;

    return (
      <button
        type="button"
        className="link-button link"
        aria-label={`View the ${countLabel} using ${treatmentName}`}
        onClick={() => openModal('TREATMENT_STUDIES', { treatmentName, studies })}
        data-testid={testId}
      >
        {countLabel}
      </button>
    );
  }

  return (
    <span className="dark-gray" data-testid={testId}>
      Unused
    </span>
  );
};

export default TreatmentStudyUsage;
